import React, { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import {
	ConfluenceExperimentsContext,
	type TemplateXflowModalSourceContext,
} from '@post-office/growth-common/confluence-experiments-context';

import EmojiResource from '@atlaskit/emoji/emoji-resource';
import { SOURCE_COMPONENT } from '@atlassian/twc-xflow-modal/constants';

import { LoadableLazy } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { getPageDataFromUrl } from './hooks/getPageDataFromUrl.ts';
import { usePageRecommendationData } from './hooks/usePageRecommendationData.tsx';
import { useInlineReactionsData } from './hooks/useInlineReactionsData.ts';
import { useCurrentPageTemplateData } from './hooks/useCurrentPageTemplateData.ts';

const TwcXflowModal = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TwcXflowModal" */ '@atlassian/twc-xflow-modal/main'
			)
		).default,
});

export const ConfluenceExperimentsProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { userId, cloudId, locale, orgId } = useSessionData();
	const currentPageTemplateData = useCurrentPageTemplateData();
	const [modalSourceContext, setModalSourceContext] = useState<
		TemplateXflowModalSourceContext | undefined
	>();
	const openTwcXflowModal = useCallback((sourceContext: TemplateXflowModalSourceContext) => {
		setModalSourceContext(sourceContext);
	}, []);
	const closeTwcXflowModal = useCallback(() => setModalSourceContext(undefined), []);
	const templateXflow = useMemo(
		() => ({ ...currentPageTemplateData, openTwcXflowModal }),
		[currentPageTemplateData, openTwcXflowModal],
	);

	const experimentsContextValue = useMemo(() => {
		return {
			templateXflow,
			usePageRecommendationData,
			useInlineReactionsData,
			getPageDataFromUrl,
			emojiProvider: getEmojiProvider(EmojiResource, {
				userId,
				cloudId,
			}),
		};
	}, [userId, cloudId, templateXflow]);

	return (
		<ConfluenceExperimentsContext.Provider value={experimentsContextValue}>
			{children}
			{cloudId && modalSourceContext ? (
				<TwcXflowModal
					cloudId={cloudId}
					locale={locale}
					onClose={closeTwcXflowModal}
					orgId={orgId ?? undefined}
					sourceComponent={SOURCE_COMPONENT.TWC_XFLOW_CONFLUENCE_TEMPLATE}
					sourceContext={modalSourceContext}
				/>
			) : null}
		</ConfluenceExperimentsContext.Provider>
	);
};
